// "Get the app" banner closing the booking thank-you page. Rendered by
// @majourneys/booking through the provider's `AppPrompt` slot, so the city
// sites (no slot) never see it.
//
// Phones get one button for their own store (through /app, which sends each
// phone to its store); wider screens get both badges and the QR code.
//
// "Sign in with the email you booked with" is true as written: the account's
// booking list matches guest bookings by the account's verified email, and
// every app sign-in (email code, Google, Apple) verifies it.
import { useSyncExternalStore } from "react";
import { useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Icon } from "@majourneys/icons";
import type { AppPromptProps } from "@majourneys/booking";
import { track } from "../analytics/posthog";
import { storeForUserAgent, type AppStore } from "../../lib/app-store";
import { APP_GOLD, APP_NAVY, APP_NAVY_GRADIENT, AppIcon, AppQr, StoreBadges } from "./app-promo-parts";

const noopSubscribe = () => () => {};

/**
 * The phone's store, read after hydration. The server has no user agent to
 * go on, so it renders the neutral "Get the app" and the client swaps in the
 * store name: same markup on both sides, no mismatch.
 */
function useStore(): AppStore | null {
  return useSyncExternalStore(
    noopSubscribe,
    () => storeForUserAgent(navigator.userAgent),
    () => null,
  );
}

const GOLD_GLOW = "radial-gradient(circle, rgba(229,178,101,.35) 0%, rgba(229,178,101,0) 70%)";

export function ThankYouAppBanner({ slug, citySlug }: AppPromptProps) {
  const { t } = useTranslation();
  const { lang } = useParams({ strict: false }) as { lang?: string };
  const store = useStore();

  const onPick = (picked: AppStore | "auto") =>
    track("app_promo_store_click", { surface: "thank_you", store: picked, activity_slug: slug, city: citySlug });

  const reasons: [string, string][] = [
    ["ri-calendar-check-line", t("app_promo.thank_you_reason_booking")],
    ["ri-sparkling-2-line", t("app_promo.thank_you_reason_ai")],
    ["ri-map-2-line", t("app_promo.thank_you_reason_plan")],
  ];
  const reasonList = (
    <ul className="flex flex-col gap-2.5">
      {reasons.map(([icon, text]) => (
        <li key={icon} className="flex items-start gap-2.5 font-sans text-[14px] font-medium leading-snug text-white/85">
          <Icon name={icon} className="mt-[1px] shrink-0 text-[17px]" style={{ color: APP_GOLD }} aria-hidden />
          <span className="min-w-0">{text}</span>
        </li>
      ))}
    </ul>
  );
  const eyebrow = (
    <p className="font-sans text-[11px] font-bold uppercase tracking-[0.12em]" style={{ color: APP_GOLD }}>
      {t("app_promo.thank_you_eyebrow")}
    </p>
  );
  const title = t("app_promo.thank_you_title");
  const note = t("app_promo.thank_you_note");

  const phoneLabel =
    store === "ios"
      ? t("app_promo.app_store_label")
      : store === "android"
        ? t("app_promo.play_label")
        : t("app_promo.header_button");
  const phoneIcon = store === "ios" ? "ri-apple-fill" : store === "android" ? "ri-google-play-fill" : "ri-download-2-line";

  return (
    <section
      data-testid="thank-you-app-banner"
      aria-label={title}
      className="relative overflow-hidden rounded-[28px] text-white [&_*]:[text-wrap:balance]"
      style={{ background: APP_NAVY_GRADIENT }}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -end-16 -top-16 h-48 w-48 rounded-full md:-top-24 md:end-auto md:start-1/3 md:h-72 md:w-72"
        style={{ background: GOLD_GLOW }}
      />

      {/* Phone: one button, for the store this phone uses. */}
      <div className="relative p-5 md:hidden">
        <div className="flex items-start gap-4">
          <div className="min-w-0 flex-1">
            {eyebrow}
            <h2 className="mt-1 font-heading text-[21px] font-bold leading-tight">{title}</h2>
          </div>
          <AppIcon size={48} />
        </div>
        <div className="mt-4">{reasonList}</div>
        <a
          href={`/${lang ?? "en"}/app`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => onPick(store ?? "auto")}
          data-testid="thank-you-app-phone"
          className="mj-press mt-5 flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-white font-sans text-[15px] font-bold"
          style={{ color: APP_NAVY }}
        >
          <Icon name={phoneIcon} className="text-[19px]" aria-hidden />
          {phoneLabel}
        </a>
        <p className="mt-3 text-center font-sans text-[12.5px] font-medium text-white/70">{note}</p>
      </div>

      {/* Tablet and up: both badges, plus the QR code for the phone. */}
      <div className="relative hidden items-end gap-8 px-8 pt-7 md:flex">
        <div className="w-[150px] shrink-0 self-end overflow-hidden rounded-t-[26px] border-[5px] border-b-0 border-[#2b2860] bg-black">
          <img
            src="/images/app/home.webp"
            alt=""
            width={150}
            height={230}
            loading="lazy"
            decoding="async"
            className="block h-[230px] w-full object-cover object-top"
          />
        </div>
        <div className="min-w-0 flex-1 pb-7">
          {eyebrow}
          <h2 className="mt-1 font-heading text-[26px] font-bold leading-tight">{title}</h2>
          <div className="mt-4">{reasonList}</div>
          <div className="mt-5">
            <StoreBadges height={44} onPick={onPick} />
          </div>
          <p className="mt-3 font-sans text-[12.5px] font-medium text-white/70">{note}</p>
        </div>
        <div className="hidden shrink-0 flex-col items-center gap-2 self-center pb-7 lg:flex">
          <div className="rounded-2xl bg-white p-1.5">
            <AppQr size={100} />
          </div>
          <p className="font-sans text-[12px] font-semibold text-white/75">{t("app_promo.scan")}</p>
        </div>
      </div>
    </section>
  );
}
